import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { Callout } from "../blocks/BlockRenderer";
import { ScoreRing } from "../svg/ScoreRing";

import { courseTheme } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

type Complete = Extract<Screen, { kind: "complete" }>;

// Course completion screen: first-try quiz score, course progress, up-next.
//
// No certificate is rendered here. The real Certificate of Completion is issued
// server-side when the whole BUNDLE finishes (rn_orientation_completions) and
// printed on the Nurse Profile packet — an in-app mock would look official
// without being the record.

export function CompleteScreen({
    screen,
    score
}: {
    screen: Complete;
    score: { correct: number; total: number; pct: number; color: string };
}) {
    return (
        <View>
            <View style={styles.done}>
                <View style={styles.ring}>
                    <ScoreRing pct={score.pct} color={score.color} size={118} />
                    <View style={styles.ringCtr}>
                        <Text style={[styles.ringPct, { color: score.color }]}>{score.pct}%</Text>
                        <Text style={styles.ringFrac}>{`${score.correct} / ${score.total}`}</Text>
                    </View>
                </View>
                <Text style={styles.heading}>{screen.heading}</Text>
                <Text style={styles.body}>{screen.body ?? ""}</Text>
            </View>

            <View style={styles.courseProg}>
                <View style={styles.cph}>
                    <Text style={styles.cphLabel}>Course progress</Text>
                    <Text style={styles.cphPct}>{screen.courseProgressLabel ?? ""}</Text>
                </View>
                <View style={styles.cpbar}>
                    <View
                        style={[
                            styles.cpfill,
                            { width: `${screen.courseProgressPct ?? 0}%` as const }
                        ]}
                    />
                </View>
                <Text style={styles.cpnums}>{screen.courseProgressNote ?? ""}</Text>
            </View>

            {screen.upNext ? (
                <View style={styles.nextup}>
                    <View style={styles.nn}>
                        <Text style={styles.nnText}>{screen.upNext.n}</Text>
                    </View>
                    <View>
                        <Text style={styles.nt}>Up next</Text>
                        <Text style={styles.nm}>{screen.upNext.title}</Text>
                    </View>
                </View>
            ) : null}

            {screen.callout ? (
                <Callout tone={screen.callout.tone} text={screen.callout.text} />
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    done: { alignItems: "center", paddingTop: 8 },
    ring: { width: 118, height: 118, alignItems: "center", justifyContent: "center" },
    ringCtr: { position: "absolute", alignItems: "center", justifyContent: "center" },
    ringPct: { fontSize: 26, fontWeight: "800" },
    ringFrac: { fontSize: 12, fontWeight: "600", color: courseTheme.slateL, marginTop: 1 },
    heading: { fontSize: 22, fontWeight: "800", color: courseTheme.navy, marginTop: 16 },
    body: {
        fontSize: 14,
        lineHeight: 21,
        color: courseTheme.slate,
        textAlign: "center",
        marginTop: 8,
        paddingHorizontal: 6
    },
    courseProg: {
        marginTop: 20,
        borderWidth: 1,
        borderColor: courseTheme.line,
        borderRadius: 16,
        padding: 15
    },
    cph: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
    cphLabel: { fontSize: 13, fontWeight: "700", color: courseTheme.navy },
    cphPct: { fontSize: 13, fontWeight: "800", color: courseTheme.tealD },
    cpbar: {
        height: 8,
        borderRadius: 99,
        backgroundColor: courseTheme.line,
        overflow: "hidden",
        marginTop: 10
    },
    cpfill: { height: "100%", borderRadius: 99, backgroundColor: courseTheme.teal },
    cpnums: { fontSize: 12, color: courseTheme.slateL, marginTop: 8 },
    nextup: {
        flexDirection: "row",
        alignItems: "center",
        gap: 13,
        marginTop: 14,
        borderWidth: 1,
        borderColor: courseTheme.tealTint2,
        backgroundColor: courseTheme.tealTint,
        borderRadius: 16,
        padding: 15
    },
    nn: {
        width: 40,
        height: 40,
        borderRadius: 11,
        backgroundColor: "#FFFFFF",
        borderWidth: 1,
        borderColor: courseTheme.tealTint2,
        alignItems: "center",
        justifyContent: "center"
    },
    nnText: { fontSize: 16, fontWeight: "800", color: courseTheme.tealD },
    cert: {
        marginTop: 18,
        borderRadius: 18,
        borderWidth: 1.5,
        borderColor: courseTheme.tealTint2,
        backgroundColor: courseTheme.tealTint,
        padding: 20,
        alignItems: "center"
    },
    certHeading: {
        fontSize: 11,
        fontWeight: "800",
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: courseTheme.tealD
    },
    certSubtitle: { fontSize: 20, fontWeight: "800", color: courseTheme.navy, marginTop: 6 },
    certLine: {
        fontSize: 12.5,
        color: courseTheme.slate,
        marginTop: 4,
        textAlign: "center"
    },
    certDivider: {
        height: 1,
        alignSelf: "stretch",
        backgroundColor: courseTheme.tealTint2,
        marginVertical: 14
    },
    certRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignSelf: "stretch",
        marginBottom: 6
    },
    certFieldLabel: { fontSize: 12, fontWeight: "600", color: courseTheme.slate },
    certFieldValue: { fontSize: 13, fontWeight: "800", color: courseTheme.navy },
    certFine: {
        fontSize: 10.5,
        color: courseTheme.slateL,
        textAlign: "center",
        marginTop: 12,
        lineHeight: 15
    },
    certBrand: { fontSize: 13, fontWeight: "800", color: courseTheme.tealD, marginTop: 10 },
    nt: {
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: courseTheme.tealD
    },
    nm: { fontSize: 15, fontWeight: "800", color: courseTheme.navy, marginTop: 1 }
});
